import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { CircularProgress, Link } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import { BGC_FIELDS } from 'utils/constants/bgcFields'
import { formatDateTime, maskString } from 'utils/functions'
import { get } from 'services/api'
import { APPLICATION_STATUS, ITEM_STATUS } from 'utils/constants/application'
import ApplicationLogButton from 'components/ResumeTable/ApplicationLogButton'
import DownloadAppButton from 'components/ResumeTable/DownloadAppButton'
import ApplicationUpdateButton from 'components/ResumeTable/ApplicationUpdateButton'
import CreateMissingCreditAnalysisButton from 'components/ResumeTable/CreateMissingCreditAnalysisButton'
import ApplicationReviewsButton from 'components/ResumeTable/AppliactionReviewsButton'
import Colors from 'utils/Colors'
import ApplicationHelpButton from 'components/ResumeTable/ApplicationHelpButton'
import ReviewsList from 'components/ManualAnalysisList/ReviewsList'
import { reportError } from 'utils/errorReporter'
import _ from "lodash"
import ReviewsCompleteList from 'components/ManualAnalysisList/ReviewsCompleteList'
import BgcExpansible from './BgcExpansible'
import styles from './styles'
import BuildScoreBoard from './BuildScoreBoard'

const   ExpandedInfo = (props) => {
  const { application_id } = props
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)

  const [data, setData] = useState(null)
  const [reviewsList, setReviewsList] = useState([])
  const [userAreaReviewsList, setUserAreaReviewsList] = useState([])
  const [reviewsLoading, setReviewsLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [updateRequested, setUpdateRequested] = useState(false)
  const [showUpdateLink, setShowUpdateLink] = useState(false)
  const manualReviewsSectionRef = useRef(null)

  const loggedUserRole = useSelector((state) => state.auth.user_role)
  const isInDenyList = data?.candidate?.person?.is_on_deny_list

  const buildInfo = (title, info, color) => {
    return (
      <div>
        <strong>{`${title}: `}</strong>
        {color == null ? info || 'N/A' : <strong style={{ color: color }}>{info || 'N/A'}</strong>}
      </div>
    )
  }

  const buildGreenhouseStatus = (greenhouseStatus) => {
      if (greenhouseStatus === null) {
        return null
      }
      const status = { active: 'Active: Em andamento', rejected: 'Rejected: Rejeitado(a)', hired: 'Hired: Contratado(a)' }

    return <div> 
      <strong>Status da Contratação da Greenhouse: </strong>
      {status[greenhouseStatus] || greenhouseStatus || 'N/A'}
  </div>  
  }


  const getReviews = async () => {
    setReviewsLoading(true)
    get({
      url: `/reviews/paginated?application_uuid=${application_id}&size=30`,
      token,
    }).then((res) => {
      const items = res?.items || []
      setReviewsList(items)
      setUserAreaReviewsList(_.filter(items, (i => i.user_role === loggedUserRole)))
    }).catch(e => {
      reportError(e)
    }).finally(() => {
      setReviewsLoading(false)
    })

    
  }

  const getData = async () => {
    setLoading(true)

    const res = await get({
      url: `/application?application_uuid=${application_id}`,
      token,
    })

    setData(res)

    setLoading(false)
    getReviews()

  }

  const scrollToManualReviews = () => {
    manualReviewsSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }


  const buildCandidateInfo = () => {
    try {
      return (
        <div className={classes.info}>

          {buildInfo('ID Greenhouse', data.candidate.greenhouse_id)}

          {buildInfo('Nome', data.candidate.person.name)}

          {buildInfo('CPF', maskString(data.candidate.person.cpf, 'cpf'))}

          {buildInfo('Nome da Mãe', data.candidate.person.filiation)}

          {buildInfo(
            'Data de Nascimento',
            data.candidate.person.birth_date
              ? moment(data.candidate.person.birth_date).format('DD/MM/YYYY')
              : null,
          )}

        {buildInfo('Recrutador', data.candidate?.recruiter_name || 'Informação indisponível')}

        </div>
      )
    } catch (err) {
      reportError(err)
      return (
        <div className={classes.errorCaptured}>
          <span>Ocorreu um erro ao tratar os dados</span>
        </div>
      )
    }
  }

  const createStatusInfo = () => {
    return data?.detailed_status?.description
  }
  
  const buildApplicationInfo = () => {
    const applicationStatusColor = () => {
      if (data?.result === 'APPROVED') {
        return Colors.green
      }
      if (data?.result === 'REPROVED') {
        return Colors.red
      }
      return null
    }
    try {
      return (
        <React.Fragment>
          {buildInfo('ID Greenhouse', data.greenhouse_id)}

          <br/>

          {buildInfo('Área', data.occupation_area)}

          {buildInfo('Senioridade', data.job_role)}
          
          {buildInfo('Estágio atual', data.current_stage)}

          <br />

          {buildInfo('Status', createStatusInfo(), applicationStatusColor())}

          {buildGreenhouseStatus(data.greenhouse_status)}


          {!isInDenyList && <div className={classes.score}>
            <div>
              <strong>Pontuação parcial: </strong>
              {data.score_board.score_data
                ? `${
                    data.score_board.score_data[BGC_FIELDS.job_role].value +
                    data.score_board.score_data[BGC_FIELDS.occupation_area]
                      .value
                  } ponto(s)`
                : 'N/A'}
            </div>
          </div>}
        </React.Fragment>
      )
    } catch (err) {
      reportError(err)
      return (
        <div className={classes.errorCaptured}>
          <span>Ocorreu um erro ao tratar os dados</span>
        </div>
      )
    }
  }

  useEffect(() => {
    getData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application_id])

  useEffect(() => {
    if (!updateRequested) {
      return undefined
    }

    const timeout = setTimeout(() => setShowUpdateLink(true), 6000)

    return () => clearTimeout(timeout)
  }, [updateRequested])


  const shouldShowField = (field) => {
    if (isInDenyList) {
      return field === BGC_FIELDS.compliance_list
    }
    return field !== BGC_FIELDS.job_role &&
           field !== BGC_FIELDS.occupation_area &&
           field !== BGC_FIELDS.risk_score
  }


  return (
    <div className={classes.root}>
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={64} color="primary" />
          <span className={classes.loadingText}>
            Buscando informações da aplicação...
          </span>
        </div>
      ) : data ? (
        <div className={classes.content}>
          <div className={classes.fixedHeader}>
            <div className={classes.headerButtonRow}>

              <div className={classes.leftHeaderButtonRow}>
                
                <ApplicationReviewsButton 
                  application={data}
                  loadingReviewsCount={reviewsLoading} 
                  reviewsCount={userAreaReviewsList?.filter((r) => r.decision === 'WAITING_DECISION')?.length || 0}
                  onClick={scrollToManualReviews}
                  useTitle={true} 
                />

                <ApplicationLogButton application={data} useTitle={true} /> 
              
                <DownloadAppButton applicationUUID={data.uuid}  useTitle={true}/>


                <ApplicationHelpButton  useTitle={true}/>

                { showUpdateLink && 
                  <span style={{ marginBottom: 5 }}>
                    <Link href='' style={{ color: 'blue' }}>Os dados podem ter sido atualizados. Recarregue a página </Link>
                  </span>
                }


              </div>

              <div className={classes.rightHeaderButtonRow}>
                

                { !updateRequested && <span style={{ marginBottom: 5 }}><b>Última atualização: </b>{formatDateTime(data.last_changed_date)}</span> }


                {data.status === APPLICATION_STATUS.bgcProcessedMissingCreditAnalysis && (
                  <span>
                  <CreateMissingCreditAnalysisButton application={data} onUpdateDataRequest={() => setUpdateRequested(true)} useTitle={true}/>
                </span>)}  

                <span>
                  <ApplicationUpdateButton application={data} onUpdateDataRequest={() => setUpdateRequested(true)} useTitle={true}/>
                </span>
              </div>
            </div>
            <hr />
          </div>

          <section className={classes.firstSection}>
            <div className={classes.sectionTitle}>Dados do Candidato</div>

            {buildCandidateInfo()}
          </section>

          <section className={classes.section} style={{ scrollMarginTop: 100 }}>
            <div className={classes.sectionTitle}>Dados da Aplicação</div>

            <div className={classes.info}>{buildApplicationInfo()}</div>
          </section>

          <section className={classes.section}>
            <div className={classes.sectionTitle}>
              Background Check
              {data.background_check?.status === ITEM_STATUS.processing && (
                <AccessTimeIcon style={{ color: '#FFF' }} />
              )}
            </div>

            {data.background_check ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <BuildScoreBoard bgcData={data.background_check} scoreBoardData={data.score_board} isInDenyList={isInDenyList} />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 20,
                }}
              >
                A aplicação ainda não atingiu o estágio de Background Check
              </div>
            )}

            {data.background_check &&
              data.background_check.status === 'PROCESSED' && (
                <div className={classes.bgc}>
                    {Object.values(BGC_FIELDS).map((field) => {
                      try {
                        return shouldShowField(field) ? (
                          <BgcExpansible
                            key={`bgc:${field}`}
                            field={field}
                            full_bgc_data={data.background_check}
                            bgc_info={data.background_check[field]}
                            inDenyList={isInDenyList}
                            score_data={data.score_board.score_data[field]} />
                            
                        ) : null
                      } catch (err) {
                        reportError(err)
                        return (
                          <div className={classes.errorCaptured}>
                            <span>Ocorreu um erro ao tratar os dados</span>
                          </div>
                        )
                      }
                    })}
                  </div>
                
              )}
          </section>

          <section  ref={manualReviewsSectionRef} className={classes.section}>
            <div className={classes.sectionTitle}>Revisões Manuais</div>

            <div>
              {reviewsLoading && (
                <div className={classes.reviewsContainer}>
                  <br />
                  <CircularProgress />
                  <br />
                  <p>Carregando revisões manuais...</p>
                </div>
              )}
              {!reviewsLoading && _.isEmpty(userAreaReviewsList) && (
                <div className={classes.reviewsContainer}>
                  {' '}
                  <br></br>
                  <p>Nenhuma revisão foi derivada para a sua área ({loggedUserRole}){_.isEmpty(reviewsList) ? ', nem para outras áreas' : ''}.</p>
                  <br />
                </div>
              )}
              {!_.isEmpty(userAreaReviewsList) && (
                <React.Fragment>
                  <br />

                  <ReviewsList reviews={userAreaReviewsList} splitReviewsByArea={false} hideTitle={true} onDataChanged={() => getReviews()} />
                  <br />
                  <br />


                </React.Fragment>
              )}
              {!_.isEmpty(reviewsList) && (
                <React.Fragment>
                  <hr></hr>
                  <br />
                  <ReviewsCompleteList bgcReviews={reviewsList} hideHelpButton={true} onDataChange={() => getReviews()} />
                </React.Fragment>
              )}
            </div>
          </section>
            
        </div>
      ) : (
        <div className={classes.notFound}>Nenhuma informação encontrada.</div>
      )}
    </div>
  )
}

export default ExpandedInfo
